// ** React Imports
import { Fragment, lazy } from "react"
import { Navigate } from "react-router-dom"
// ** Layouts
import BlankLayout from '../../@core/layouts/BlankLayout'
import VerticalLayout from "../../layouts/VerticalLayout"
import HorizontalLayout from "../../layouts/HorizontalLayout"
import LayoutWrapper from "../../@core/layouts/components/layout-wrapper"
// ** Route Components
import PublicRoute from "../../@core/components/routes/PublicRoute"

// ** Utils
import { isObjEmpty, isUserLoggedIn } from "../../utility/Utils"

const Workorder = lazy(() => import("../../views/Workorder"))
const MyProfile = lazy(() => import("../../views/MyProfile"))
const Home = lazy(() => import("../../views/Dashboard/Home"))
const Dashboard = lazy(() => import("../../views/Dashboard/CommonDashboard"))
const CustomerDashboard = lazy(() => import("../../views/Dashboard/CustomerDashboard"))
const Unauthorized = lazy(() => import("../../views/Pages/Unauthorized"))
const EmployeeDashbaord = lazy(() => import("../../views/Dashboard/EmployeeDashboard"))
const Error = lazy(() => import("../../views/Pages/Error"))
const PageRefresh = lazy(() => import("../../views/PageRefresh"))
const ApostillePermissions = lazy(() => import("../../views/Dashboard/ResponsibleOffice"))
const ApostileLogs = lazy(() => import("../../views/Dashboard/Log"))
const UserManagement = lazy(() => import("../../views/Dashboard/UserManagement"))

const getLayout = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />,
  horizontal: <HorizontalLayout />
}

// ** Document title
// const TemplateTitle = "%s - Vuexy React Admin Template"
const TemplateTitle = 'Corporate'
// ** Default Route
let DefaultRoute = "/search"

let Routes = []

const getRouteMeta = (route) => {
  if (isObjEmpty(route.element.props)) {
    if (route.meta) {
      return { routeMeta: route.meta }
    } else {
      return {}
    }
  }
}

// ** Return Filtered Array of Routes & Paths
const MergeLayoutRoutes = (layout, defaultLayout) => {
  const LayoutRoutes = []
  if (isUserLoggedIn()) {
    if (JSON.parse(isUserLoggedIn()).isEmployee) {
      let sessionRoute = sessionStorage.getItem('UrlPath')
      if (sessionRoute === '/Unauthorized') {
        const userData = JSON.parse(localStorage.getItem('userData'))
        const userRoles = userData && userData.userRoles
        if (userRoles && userRoles.length > 0) {
          sessionRoute = '/home'
        }
      }

      DefaultRoute = sessionRoute ? sessionRoute : '/home'
      // **Employee Merge Routes
      const employeeRoutes = [
        {
          path: "/",
          index: true,
          element: <Navigate replace to={DefaultRoute} />
        },
        {
          path: "/home",
          element: <Home />
        },
        {
          path: "/search",
          element: <EmployeeDashbaord />
        },
        {
          path: "/:catCode/:typeCode/save",
          element: <Workorder />
        },
        {
          path: "/:catCode/:typeCode/:woId",
          element: <Workorder />
        },
        {
          path: "/role-management",
          element: <UserManagement />
        },
        {
          path: "/my-profile",
          element: <MyProfile />
        },
        {
          path: "/dashboard",
          element: <Dashboard />
        },
        {
          path: '/pageRefresh',
          element: <PageRefresh />
        },
        {
          path: '/Unauthorized',
          element: <Unauthorized />
        },
        {
          path: "*",
          element: <Error />
        }
      ]
      const userData = JSON.parse(localStorage.getItem('userData'))
      const userRoles = userData?.userRoles
      const isAdmin = userRoles?.some(find => find.rId === 1)
      if (isAdmin) {
        employeeRoutes.push({
          path: "/apostillePermissions",
          element: < ApostillePermissions />
        })
        employeeRoutes.push({
          path: "/apostilleLogs",
          element: < ApostileLogs />
        })
      } else {
        const superWORW = userRoles?.some(find => (find.rId === 2 || find.rId === 3))
        const WORW = userRoles?.some(find => (find.wtId === 3 || find.wtId === 4))
        if (superWORW || WORW) {
          employeeRoutes.push({
            path: "/apostilleLogs",
            element: < ApostileLogs />
          })
        }
      }
      Routes = employeeRoutes
    } else {
      // **Customer Merge Routes
      let sessionRoute = sessionStorage.getItem('UrlPath')
      if (sessionRoute === '/Unauthorized') {
        sessionRoute = '/home'
      }
      DefaultRoute = sessionRoute ? sessionRoute : '/home'
      const customerRoutes = [
        {
          path: "/",
          index: true,
          element: <Navigate replace to={DefaultRoute} />
        },
        {
          path: "/home",
          element: <Home />
        },
        {
          path: "/search",
          element: <CustomerDashboard />
        },
        {
          path: "/:catCode/:typeCode/save",
          element: <Workorder />
        },
        {
          path: "/:catCode/:typeCode/:woId",
          element: <Workorder />
        },
        {
          path: "/dashboard",
          element: <Dashboard />
        },
        {
          path: "/my-profile",
          element: <MyProfile />
        },
        {
          path: '/pageRefresh',
          element: <PageRefresh />
        },
        {
          path: '/Unauthorized',
          element: <Unauthorized />
        },
        {
          path: "*",
          element: <Error />
        }
      ]
      Routes = customerRoutes
    }
  }
  if (Routes) {
    Routes.filter((route) => {
      let isBlank = false
      // ** Checks if Route layout or Default layout matches current layout
      if (
        (route.meta && route.meta.layout && route.meta.layout === layout) ||
        ((route.meta === undefined || route.meta.layout === undefined) &&
          defaultLayout === layout)
      ) {
        const RouteTag = PublicRoute

        // ** Check for public or private route
        if (route.meta) {
          route.meta.layout === "blank" ? (isBlank = true) : (isBlank = false)
        }
        if (route.element) {
          const Wrapper =
            // eslint-disable-next-line multiline-ternary
            isObjEmpty(route.element.props) && isBlank === false
              ? // eslint-disable-next-line multiline-ternary
              LayoutWrapper
              : Fragment

          route.element = (
            <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
              <RouteTag route={route}>{route.element}</RouteTag>
            </Wrapper>
          )
        }

        // Push route to LayoutRoutes
        LayoutRoutes.push(route)
      }
      return LayoutRoutes
    })
  }
  return LayoutRoutes
}

const getRoutes = (layout) => {
  const defaultLayout = layout || "vertical"
  const layouts = ["vertical", "horizontal", "blank"]

  const AllRoutes = []

  layouts.forEach((layoutItem) => {
    const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout)

    AllRoutes.push({
      path: "/",
      element: getLayout[layoutItem] || getLayout[defaultLayout],
      children: LayoutRoutes
    })
  })
  return AllRoutes
}

export { DefaultRoute, TemplateTitle, Routes, getRoutes }
